import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ProductPolicy } from 'src/app/models/product/product-policy';
import { ProductPolicyEditDto } from 'src/app/models/product/product-policy-edit';
import { ProductPolicyRequestDto } from "src/app/models/product/product-policy-request";
import { ProductPolicyResponseDto } from "src/app/models/product/product-policy-response";
import { environment } from 'src/environments/environment';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  })
};

@Injectable({
  providedIn: 'root'
})
export class ProductPolicyService {
  
  private apiUrl = environment.apiUrl;

  /**
   * Tracks the ID of the most recently edited eligibility (product policy).
   * view-plans uses this to detect when to refresh the search results.
   */
  private lastEditedEligibilityId: number | null = null;

  /**
   * Caches the last countries list used in view-plans for Eligibility search.
   * When user returns from editing, we re-apply the same search to get fresh data.
   */
  private lastSearchCountries: string[] | null = null;

  constructor(
    private httpClient: HttpClient,
    private messageService: MessageService
  ) {
  }

  /**
   * Returns the ID of the most recently edited eligibility (if any).
   * Clears the value after retrieval (one-time check).
   */
  getAndClearLastEditedEligibilityId(): number | null {
    const id = this.lastEditedEligibilityId;
    this.lastEditedEligibilityId = null;
    return id;
  }

  /**
   * Saves the countries list used in view-plans for Eligibility search.
   * This allows automatic re-search when user returns from editing.
   */
  cacheSearchCountries(countries: string[]): void {
    this.lastSearchCountries = countries ? [...countries] : null;
  }

  /**
   * Returns the cached countries list (if any).
   */
  getCachedSearchCountries(): string[] | null {
    return this.lastSearchCountries;
  }

  getAllProductPoliciesByCountry(countries: string[]): Observable<ProductPolicy[]> {
    return this.httpClient.post<ProductPolicy[]>(`${this.apiUrl}/api/v1/products/policies/countries`, countries, httpOptions)
      .pipe(
        map((response: ProductPolicy[]) => {
          return response;
        })
      );
  }
  
  getAllProductPolicies(): Observable<ProductPolicy[]> {
    return this.httpClient.get<ProductPolicy[]>(`${this.apiUrl}/api/v1/products/policies`)
      .pipe(
        map((response: ProductPolicy[]) => {
          return response;
        })
      );
  }

  save(requestDto: ProductPolicyRequestDto): Observable<ProductPolicyResponseDto> {
    return this.httpClient.post(`${this.apiUrl}/api/v1/products/policies`, requestDto, httpOptions)
      .pipe(
        map((response: ProductPolicyResponseDto) => {
          return response;
        })
      );
  }

  getById(id: number):  Observable<ProductPolicyEditDto>{
  return this.httpClient.get(`${this.apiUrl}/api/v1/products/policies/id/${id}`)
  .pipe(
    map((response: ProductPolicyEditDto) => {
      return response;
    })
  );
}

updateProductPolicy(productPolicy :ProductPolicyEditDto): Observable<ProductPolicyResponseDto>{
  return this.httpClient.put(`${this.apiUrl}/api/v1/products/policies`, productPolicy, httpOptions)
      .pipe(
        tap((_updatedPolicy) => {
          // Track that this eligibility was just edited
          // Note: ProductPolicyEditDto uses 'productPolicyId', not 'id'
          if (productPolicy && typeof productPolicy === 'object' && 'productPolicyId' in productPolicy) {
            this.lastEditedEligibilityId = (productPolicy as unknown as Record<string, unknown>).productPolicyId as number | null;
          }
        }),
        map((response: ProductPolicyResponseDto) => {
          return response;
        })
      );
}

}
